import {
  AddressDisplay,
  Badge,
  Button,
  Glass,
  Skeleton,
  Tooltip,
} from "@gotts.ai/ui";

import { AnimatedBalance } from "../../components/AnimatedBalance";
import { CompositionBar } from "../../components/CompositionBar";
import { HookFlagsGrid } from "../../components/HookFlagsGrid";
import { TokenIcon } from "../../components/TokenIcon";
import { useDevenvNavigate } from "../../hooks/useDevenvNavigate";

import { decodeSqrtPriceX96, getDecimals } from "./types";

import type { V4PoolState } from "./types";
import type { Address } from "viem";

const ZERO_ADDR = "0x0000000000000000000000000000000000000000" as Address;

export function V4PoolCard({
  pair,
  fee,
  tickSpacing,
  tokenAddresses,
  state,
  tokenSymbols,
  hooks,
}: {
  pair: string;
  fee: string;
  tickSpacing: number;
  tokenAddresses: [string, string] | null;
  state: V4PoolState | null;
  tokenSymbols: readonly [string, string];
  hooks?: Address;
}) {
  const navigate = useDevenvNavigate();
  const decimals0 = getDecimals(tokenSymbols[0]);
  const decimals1 = getDecimals(tokenSymbols[1]);
  const needsInvert = tokenAddresses
    ? tokenAddresses[0].toLowerCase() > tokenAddresses[1].toLowerCase()
    : false;
  const sortedDecimals0 = needsInvert ? decimals1 : decimals0;
  const sortedDecimals1 = needsInvert ? decimals0 : decimals1;

  const isUninitialized = state?.sqrtPriceX96 === 0n;
  const hasHook = hooks && hooks !== ZERO_ADDR;

  return (
    <Glass className="glass-card-interactive cursor-pointer p-3">
      {/* Header */}
      <div className="mb-1 flex items-center gap-1.5">
        <div className="flex -space-x-1.5">
          <TokenIcon symbol={tokenSymbols[0]} size={20} />
          <TokenIcon symbol={tokenSymbols[1]} size={20} />
        </div>
        <span className="text-gold font-mono text-sm font-semibold">
          {pair}
        </span>
      </div>
      <div className="mb-2 text-xs text-[#9ca3af]">
        Hook-based | Fee: {fee} | Tick Spacing: {tickSpacing}
        {hasHook && " | Hook"}
      </div>

      {state ? (
        <div className="space-y-3">
          {isUninitialized ? (
            <div className="bg-amber-400/[0.04] p-3 text-center">
              <Tooltip content="This pool has been created but has no liquidity. Add liquidity to activate it.">
                <span>
                  <Badge variant="warning">Uninitialized</Badge>
                </span>
              </Tooltip>
              <div className="mt-2 text-[11px] text-white/40">
                Awaiting first liquidity deposit
              </div>
              <CompositionBar
                value0={0}
                value1={0}
                label0={tokenSymbols[0].toUpperCase()}
                label1={tokenSymbols[1].toUpperCase()}
                height={6}
              />
            </div>
          ) : (
            <>
              {/* Price */}
              <div className="text-text-muted text-xs">
                Price:{" "}
                <span className="text-text-dim font-mono">
                  1 {tokenSymbols[0].toUpperCase()} ={" "}
                  {decodeSqrtPriceX96(
                    state.sqrtPriceX96,
                    sortedDecimals0,
                    sortedDecimals1,
                    needsInvert,
                  )}{" "}
                  {tokenSymbols[1].toUpperCase()}
                </span>
              </div>

              <div className="text-text-muted text-xs">
                Tick:{" "}
                <span className="text-text-dim font-mono">{state.tick}</span>
              </div>
              <div className="text-text-muted text-xs">
                Liquidity:{" "}
                <AnimatedBalance
                  value={Number(state.liquidity)}
                  className="text-text-dim"
                />
              </div>

              <CompositionBar
                value0={50}
                value1={50}
                label0={tokenSymbols[0].toUpperCase()}
                label1={tokenSymbols[1].toUpperCase()}
                height={6}
              />
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
        </div>
      )}

      {/* Hook details */}
      {hasHook && (
        <div className="mt-2 space-y-1.5 border-t border-[var(--glass-border)] pt-2">
          <div className="text-text-muted flex items-center gap-1 text-xs">
            <span>Hook:</span>
            <AddressDisplay address={hooks} fontSize={10} copyable />
          </div>
          <HookFlagsGrid hookAddress={hooks} />
        </div>
      )}

      {/* Token addresses */}
      {tokenAddresses && (
        <div className="mt-2 space-y-1">
          <div className="text-text-muted flex items-center gap-1 text-xs">
            <span>A:</span>
            <AddressDisplay
              address={tokenAddresses[0]}
              fontSize={10}
              copyable
            />
          </div>
          <div className="text-text-muted flex items-center gap-1 text-xs">
            <span>B:</span>
            <AddressDisplay
              address={tokenAddresses[1]}
              fontSize={10}
              copyable
            />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-2 flex gap-2 border-t border-[var(--glass-border)] pt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `swap?tokenIn=${tokenSymbols[0]}&tokenOut=${tokenSymbols[1]}&version=v4`,
            );
          }}
        >
          {"\u21C4"} Swap
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `liquidity?token0=${tokenSymbols[0]}&token1=${tokenSymbols[1]}&version=v4`,
            );
          }}
        >
          + Add Liquidity
        </Button>
      </div>
    </Glass>
  );
}
